import * as Sentry from '@sentry/react';
import isEmbedded from '@/utils/is-embedded';

interface SentryOptions {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  fingerprint?: string[];
}

/**
 * Captures an exception and sends it to Sentry.
 * Handles both embedded and standalone modes.
 *
 * @param exception - The error to capture
 * @param options - Optional additional data to attach to the Sentry event
 */
export function captureException(
  exception: unknown,
  options?: SentryOptions,
  hint: Sentry.EventHint = {},
) {
  const { tags, extra, fingerprint } = options ?? {};
  if (isEmbedded() && import.meta.env.MODE === 'production') {
    const sentryClient = window.ReactTeammateApp?.sentryClient;
    if (sentryClient) {
      const scope = new Sentry.Scope();
      if (tags) {
        scope.setTags(tags);
      }
      if (extra) {
        scope.setExtras(extra);
      }
      if (fingerprint) {
        scope.setFingerprint(fingerprint);
      }
      sentryClient.captureException(exception, { originalException: exception, ...hint }, scope);
    }
  } else {
    Sentry.withScope((scope) => {
      if (tags) {
        scope.setTags(tags);
      }
      if (extra) {
        scope.setExtras(extra);
      }
      if (fingerprint) {
        scope.setFingerprint(fingerprint);
      }
      Sentry.captureException(exception, hint);
    });
  }
}
